import { useEffect, useState, useCallback } from 'react';
import { API_BASE } from '../lib/api';
import { brandifyText } from '../lib/utils';

interface CMSEntry {
  type: string;
  value: string;
  category: string;
}

const CACHE_KEY = 'x1493_cms_cache';
const CACHE_TTL_MS = 60_000; // 1 minute

function getCached(): Record<string, CMSEntry> | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { ts: number; data: Record<string, CMSEntry> };
    if (Date.now() - parsed.ts > CACHE_TTL_MS) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

function setCached(data: Record<string, CMSEntry>) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
  } catch {
    // ignore
  }
}

export function useCMS() {
  const [entries, setEntries] = useState<Record<string, CMSEntry>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCMS = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const cached = getCached();
      if (cached) {
        setEntries(cached);
        setLoading(false);
        return;
      }

      const res = await fetch(`${API_BASE}/api/public/cms`);
      const json = await res.json().catch(() => ({ success: false, error: 'Invalid response' }));
      if (!res.ok || !json.success) {
        throw new Error(json.error || 'Failed to load CMS');
      }
      const data = (json.data || {}) as Record<string, CMSEntry>;
      setEntries(data);
      setCached(data);
    } catch (e: any) {
      setError(e.message || 'Failed to load CMS');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCMS();
  }, [fetchCMS]);

  const getValue = useCallback((key: string, fallback = ''): string => {
    // brandifyText is a display-only transform («X-1493»); safe for phone
    // numbers and other functional values since they never match the pattern.
    return brandifyText(entries[key]?.value ?? fallback);
  }, [entries]);

  const getHtml = useCallback((key: string, fallback = ''): { __html: string } => {
    return { __html: brandifyText(entries[key]?.value ?? fallback) };
  }, [entries]);

  const getBoolean = useCallback((key: string, fallback = false): boolean => {
    const val = entries[key]?.value;
    if (val === undefined || val === null) return fallback;
    return val === '1' || val === 'true' || val === 'yes';
  }, [entries]);

  return { entries, loading, error, getValue, getHtml, getBoolean, refresh: fetchCMS };
}
